import { useCallback, useEffect, useRef, useState } from 'react';
import { unitsApi } from '../../api/unitsApi';
import { storageUnitsApi } from '../../api/storageUnitsApi';
import { buildingKeyObjects, KeyObjectsByBuilding } from '../../Pages/KeyRegister/keyObjects';

export interface UseKeyObjectsReturn {
    /** Loaded buildings only; a building still loading or failed is absent. */
    objectsByBuilding: KeyObjectsByBuilding;
    loadingBuildings: { [buildingKey: string]: true };
}

/**
 * Loads the lägenheter, lokaler and förråd of the given buildings once each (`units` and
 * `storageUnits` are per building; there is no instance-wide list). A failed building stays
 * absent and is tried again when it is asked for anew.
 */
export const useKeyObjects = (buildingKeys: string[]): UseKeyObjectsReturn => {
    const [objectsByBuilding, setObjectsByBuilding] = useState<KeyObjectsByBuilding>({});
    const [loadingBuildings, setLoadingBuildings] = useState<{ [buildingKey: string]: true }>({});
    const requested = useRef(new Set<string>());
    const mounted = useRef(true);
    // Set on every mount: StrictMode mounts, unmounts and mounts again in development.
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const load = useCallback(async (buildingKey: string) => {
        requested.current.add(buildingKey);
        setLoadingBuildings(prev => ({ ...prev, [buildingKey]: true }));
        try {
            const [units, storageUnits] = await Promise.all([
                unitsApi.getUnits(buildingKey),
                storageUnitsApi.getUnits(buildingKey),
            ]);
            if (mounted.current) setObjectsByBuilding(prev => ({ ...prev, [buildingKey]: buildingKeyObjects(buildingKey, units, storageUnits) }));
        } catch (err) {
            console.error(`Could not load units of building ${buildingKey}`, err);
            requested.current.delete(buildingKey);
        } finally {
            if (mounted.current) {
                setLoadingBuildings(prev => {
                    const { [buildingKey]: _done, ...rest } = prev;
                    return rest;
                });
            }
        }
    }, []);

    const wanted = buildingKeys.filter(Boolean).sort().join('|');
    useEffect(() => {
        for (const buildingKey of wanted ? wanted.split('|') : []) {
            if (!requested.current.has(buildingKey)) void load(buildingKey);
        }
    }, [wanted, load]);

    return { objectsByBuilding, loadingBuildings };
};
